<script setup lang="ts">
import { ref, computed, toRef, watch, nextTick } from "vue";
import { useTranslation } from "i18next-vue";
import {
  useGetFieldParts,
  useCreateFieldPart,
} from "@/stores/queries/field-observations.query";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";

const props = defineProps<{
  diagnosticId: number;
  modelValue?: number;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: number | undefined): void;
}>();

const { t } = useTranslation();
const diagnosticId = toRef(props, "diagnosticId");

// Parts data
const { data: parts } = useGetFieldParts(diagnosticId);
const { mutateAsync: createPart } = useCreateFieldPart();

// Track expanded parts at each level (path of expanded part IDs)
const expandedPath = ref<number[]>([]);

// New part creation
const showAddPart = ref(false);
const newPartName = ref("");
const isCreatingPart = ref(false);
const creatingPartParentId = ref<number | undefined>();
const partInputRef = ref<HTMLInputElement | null>(null);

// Hierarchical parts: root parts (no parent)
const rootParts = computed(() => {
  if (!parts.value) return [];
  return parts.value.filter((p) => !p.parentId);
});

// Get children of a part
const getChildParts = (parentId: number) => {
  if (!parts.value) return [];
  return parts.value.filter((p) => p.parentId === parentId);
};

// Check if a part is in the expanded path
const isExpanded = (partId: number) => expandedPath.value.includes(partId);

// Generate levels to display (for multi-level hierarchy)
const levelsToDisplay = computed(() => {
  const levels: Array<{ level: number; parentId: number; parts: any[] }> = [];

  for (let i = 0; i < expandedPath.value.length; i++) {
    const parentId = expandedPath.value[i];
    const children = getChildParts(parentId);
    // Always add a level for expanded parts (even if no children, to show add button)
    levels.push({
      level: i + 1,
      parentId,
      parts: children,
    });
  }

  return levels;
});

// Build expanded path from a part ID (traverse up the hierarchy)
const buildExpandedPathFromPart = (partId: number | undefined): number[] => {
  if (!partId || !parts.value) return [];

  const path: number[] = [];
  let currentPart = parts.value.find((p) => p.id === partId);

  while (currentPart) {
    path.unshift(currentPart.id);
    if (currentPart.parentId) {
      currentPart = parts.value.find((p) => p.id === currentPart!.parentId);
    } else {
      break;
    }
  }

  return path;
};

// Initialize expanded path from modelValue
watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal && parts.value) {
      expandedPath.value = buildExpandedPathFromPart(newVal);
    }
  },
  { immediate: true },
);

// Also watch parts loading
watch(
  () => parts.value,
  () => {
    if (props.modelValue && parts.value) {
      expandedPath.value = buildExpandedPathFromPart(props.modelValue);
    }
  },
);

// Select/expand a part at a given level
const selectPartAtLevel = (id: number, level: number) => {
  const currentExpanded = expandedPath.value[level];

  if (currentExpanded === id) {
    // Clicking same part: collapse (remove this level and below)
    expandedPath.value = expandedPath.value.slice(0, level);
    // If we collapsed to nothing, deselect
    if (expandedPath.value.length === 0) {
      emit("update:modelValue", undefined);
    } else {
      // Select the parent level
      emit(
        "update:modelValue",
        expandedPath.value[expandedPath.value.length - 1],
      );
    }
  } else {
    // Expand this part (truncate path to this level, then add new id)
    expandedPath.value = [...expandedPath.value.slice(0, level), id];
    emit("update:modelValue", id);
  }
};

const selectRootPart = (id: number) => {
  selectPartAtLevel(id, 0);
};

const selectChildPart = (id: number, level: number) => {
  selectPartAtLevel(id, level);
};

const openAddPart = (parentId?: number) => {
  creatingPartParentId.value = parentId;
  showAddPart.value = true;
  nextTick(() => {
    partInputRef.value?.focus();
  });
};

const handleCreatePart = async () => {
  if (!newPartName.value.trim()) return;
  isCreatingPart.value = true;
  try {
    const result = await createPart({
      name: newPartName.value.trim(),
      diagnosticId: props.diagnosticId,
      parentId: creatingPartParentId.value,
    });
    emit("update:modelValue", result.id);
    // Update expanded path to include the new part
    if (creatingPartParentId.value) {
      expandedPath.value = [...expandedPath.value, result.id];
    } else {
      expandedPath.value = [result.id];
    }
    newPartName.value = "";
    showAddPart.value = false;
    creatingPartParentId.value = undefined;
  } finally {
    isCreatingPart.value = false;
  }
};
</script>

<template>
  <div class="part-selector">
    <!-- Level 0: Root parts -->
    <div class="badges-wrap">
      <button
        v-for="part in rootParts"
        :key="part.id"
        class="badge-btn expandable"
        :class="{ selected: isExpanded(part.id) }"
        @click="selectRootPart(part.id)"
      >
        {{ part.name }}
        <PhosphorIcon
          v-if="getChildParts(part.id).length > 0"
          name="caret-down"
          :size="14"
          class="ms-1 chevron-icon"
          :class="{ rotated: isExpanded(part.id) }"
        />
      </button>
      <button class="badge-btn badge-add" @click="openAddPart()">
        {{ t("dashboard:fieldObservations.part.add") }}
      </button>
    </div>

    <!-- Dynamic levels: Child parts -->
    <div
      v-for="levelData in levelsToDisplay"
      :key="levelData.level"
      class="badges-wrap badges-wrap-level mt-2"
      :style="{ marginLeft: `${(levelData.level - 1) * 12 + 8}px` }"
    >
      <button
        v-for="part in levelData.parts"
        :key="part.id"
        class="badge-btn badge-child expandable"
        :class="{ selected: isExpanded(part.id) }"
        @click="selectChildPart(part.id, levelData.level)"
      >
        {{ part.name }}
        <PhosphorIcon
          v-if="getChildParts(part.id).length > 0"
          name="caret-down"
          :size="14"
          class="ms-1 chevron-icon"
          :class="{ rotated: isExpanded(part.id) }"
        />
      </button>
      <button
        class="badge-btn badge-add"
        @click="openAddPart(levelData.parentId)"
      >
        {{
          levelData.parts.length === 0
            ? t("dashboard:fieldObservations.part.addSubPart")
            : t("dashboard:fieldObservations.part.add")
        }}
      </button>
    </div>

    <!-- Add Part Popup -->
    <Teleport to="body">
      <div
        v-if="showAddPart"
        class="popup-overlay"
        @click.self="showAddPart = false"
      >
        <div class="popup-content">
          <h4>
            {{
              creatingPartParentId
                ? t("dashboard:fieldObservations.part.createSubPart")
                : t("dashboard:fieldObservations.part.create")
            }}
          </h4>
          <input
            ref="partInputRef"
            v-model="newPartName"
            type="text"
            class="popup-input"
            :placeholder="t('dashboard:fieldObservations.part.placeholder')"
            @keyup.enter="handleCreatePart"
          />
          <div class="popup-actions">
            <button class="popup-btn cancel" @click="showAddPart = false">
              {{ t("cancel") }}
            </button>
            <button
              class="popup-btn confirm"
              :disabled="!newPartName.trim() || isCreatingPart"
              @click="handleCreatePart"
            >
              <span
                v-if="isCreatingPart"
                class="spinner-border spinner-border-sm me-2"
              />
              {{ t("add") }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped lang="scss">
.part-selector {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.badges-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  &.badges-wrap-level {
    padding-left: 12px;
    border-left: 2px solid var(--bs-border-color);
  }
}

.badge-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 20px;
  background-color: var(--bs-tertiary-bg);
  color: var(--bs-secondary-color);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;

  &:hover:not(.selected) {
    background-color: var(--bs-secondary-bg);
  }

  &.selected {
    background-color: var(--bs-primary);
    color: white;
  }

  &.expandable {
    .chevron-icon {
      transition: transform 0.2s ease;

      &.rotated {
        transform: rotate(180deg);
      }
    }
  }

  &.badge-child {
    padding: 6px 14px;
    font-size: 13px;
  }

  &.badge-add {
    padding: 8px 14px;
    background-color: transparent;
    color: var(--bs-secondary-color);
    border: 1px dashed var(--bs-secondary-color);

    &:hover {
      border-color: var(--bs-primary);
      color: var(--bs-primary);
    }
  }
}

// Popup overlay
.popup-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.popup-content {
  width: 100%;
  max-width: 400px;
  background-color: var(--bs-white);
  border-radius: 16px;
  padding: 24px;

  h4 {
    margin: 0 0 16px;
    font-size: 18px;
    font-weight: 600;
  }
}

.popup-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--bs-border-color);
  border-radius: 12px;
  background-color: var(--el-bg-color);
  font-size: 16px;
  color: var(--bs-body-color);

  &:focus {
    outline: none;
    border-color: var(--bs-primary);
  }
}

.popup-actions {
  display: flex;
  gap: 12px;
  margin-top: 20px;
}

.popup-btn {
  flex: 1;
  padding: 12px 20px;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &.cancel {
    background-color: var(--bs-secondary-bg);
    color: var(--bs-body-color);

    &:hover {
      background-color: var(--bs-tertiary-bg);
    }
  }

  &.confirm {
    background-color: var(--bs-primary);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      opacity: 0.9;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}
</style>
